import { useState } from 'react';

import { AttemptActions } from './AttemptActions';
import { describeTest, diagnose } from './channel-diagnosis';
import { DisconnectDialog } from './DisconnectDialog';
import { describeAttempt, describeRemote, EM_ANDAMENTO, stepsFor } from './onboarding-diagnosis';
import { OnboardingSteps } from './OnboardingSteps';
import { RemoteHealthBlock } from './RemoteHealthBlock';
import { useChannelHealth, useSetChannelEnabled, useTestChannel } from './use-channels';

import type { ChannelConfigWire, OnboardingAttemptWire } from './types';
import type { ReactElement } from 'react';

/**
 * ⚠️ As chaves usam **hífen**, não underscore: o serializador do Engine converte o enum
 * do Prisma (`whatsapp_cloud_api`) para o formato de fio (`whatsapp-cloud-api`) em
 * `tenant-channel-configs.controller.ts:48`. Com underscore, o `??` caía no código cru e
 * a tela mostrava `whatsapp-cloud-api` como se fosse o nome do canal — visto na
 * validação viva, invisível para os testes (que passam o objeto direto).
 */
const NOME_DO_CANAL: Record<string, string> = {
  'whatsapp-cloud-api': 'WhatsApp',
  telegram: 'Telegram',
  webchat: 'Webchat',
  email: 'E-mail',
  'instagram-dm': 'Instagram',
  simulador: 'Simulador',
};

type Tom = 'ok' | 'bad' | 'warn' | 'mute';

/** Tipado por `Tom` (e não `Record<string,…>`) para que um tom novo não compile em silêncio. */
const BORDA: Record<Tom, string> = {
  ok: 'border-l-[3px] border-l-emerald-600',
  bad: 'border-l-[3px] border-l-red-600',
  warn: 'border-l-[3px] border-l-amber-600',
  mute: 'border-l-[3px] border-l-border-subtle',
};

const PILULA: Record<Tom, string> = {
  ok: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  bad: 'bg-red-50 border-red-200 text-red-700',
  warn: 'bg-amber-50 border-amber-200 text-amber-700',
  mute: 'bg-bg-muted border-border-default text-text-secondary',
};

/** «há 3 min», «há 2 h», «15/Set» — a precisão que interessa muda com a idade. */
function quando(iso: string | null): string {
  if (iso === null) return 'nunca';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${String(min)} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${String(h)} h`;
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

/** Negrito de `**assim**` sem trazer um parser de Markdown para uma frase. */
function comDestaque(frase: string): ReactElement {
  return (
    <>
      {frase
        .split('**')
        .map((parte, i) =>
          i % 2 === 1 ? <strong key={parte + String(i)}>{parte}</strong> : parte,
        )}
    </>
  );
}

export interface ChannelCardProps {
  config: ChannelConfigWire;
  canManage: boolean;
  onTrocarCredencial: (config: ChannelConfigWire) => void;
  /** A tentativa de onboarding ligada a ESTE canal (W2), quando ainda anda. A aba é quem busca. */
  attempt?: OnboardingAttemptWire | null;
  /** «Trocar de número» terminou de desconectar: a aba abre a escolha do caminho do novo. */
  onTrocouNumero?: () => void;
}

/**
 * Um canal, com o seu estado.
 *
 * **A ordem do conteúdo é a tese da tela:** pílula, depois a **frase de diagnóstico**,
 * e só então a tabela de fatos. Quem abre esta aba já tem um problema — precisa da frase.
 * A tabela é para quem já entendeu e quer conferir.
 *
 * O `simulador` não é canal de cliente (é ferramenta de laboratório): não tem credencial
 * nem webhook, e por isso não mostra saúde. Tratá-lo como canal quebrado seria mentira.
 */
export function ChannelCard({
  config,
  canManage,
  onTrocarCredencial,
  attempt,
  onTrocouNumero,
}: ChannelCardProps): ReactElement {
  const ehSimulador = config.channel === 'simulador';
  const { data: health, isLoading } = useChannelHealth(config.id);
  const testar = useTestChannel();
  const interruptor = useSetChannelEnabled();
  const [ultimoTeste, setUltimoTeste] = useState<string | null>(null);
  // Desligar um canal tira o cliente do ar. Confirmação em DOIS CLIQUES no próprio botão,
  // e não `confirm()` do navegador: o diálogo nativo trava a página inteira e some com o
  // contexto — quem está desligando precisa continuar vendo qual canal é.
  const [confirmandoDesligar, setConfirmandoDesligar] = useState(false);
  // W2: canal ligado pelo popup tem outros gestos (o token veio do cofre, não de um campo).
  const [dialogo, setDialogo] = useState<'desconectar' | 'trocar' | null>(null);
  const ehWhatsApp = config.channel === 'whatsapp-cloud-api';
  const viaPopup = ehWhatsApp && config.config.onboardedVia === 'embedded_signup';
  // A tentativa desta conexão ainda anda (o canal existe desde o passo 1): mostrar os passos.
  const tentativaAndando =
    attempt?.channelConfigId === config.id &&
    (EM_ANDAMENTO.includes(attempt.status) ||
      attempt.status === 'failed' ||
      attempt.status === 'popup_closed' ||
      attempt.status === 'opened');
  const tentativa = tentativaAndando && attempt !== undefined && attempt !== null ? attempt : null;

  const nome = NOME_DO_CANAL[config.channel] ?? config.channel;

  if (ehSimulador) {
    return (
      <div className="mb-3 rounded-lg border border-border-default bg-surface p-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-text-primary">{nome}</span>
          <span className={`rounded-full border px-2 py-0.5 text-xs ${PILULA.mute}`}>Interno</span>
        </div>
        <p className="mt-2 text-xs text-text-secondary">
          Ferramenta de laboratório, não canal de cliente: não tem credencial nem webhook — quem
          escreve é você, daqui de dentro.
        </p>
      </div>
    );
  }

  if (isLoading || health === undefined) {
    return (
      <div className="mb-3 rounded-lg border border-border-default bg-surface p-4 text-sm text-text-secondary">
        {nome} · carregando saúde…
      </div>
    );
  }

  const d = diagnose(health);
  const remoto =
    ehWhatsApp && health.remote !== null ? describeRemote(health.remote, config.displayName) : null;

  /**
   * O cabeçalho (pílula + frase) segue a ordem da tese: tentativa andando > degradado remoto >
   * pendências do tenant > diagnóstico local. Uma função em vez de ternários aninhados, para
   * cada ramo ser legível.
   */
  function cabecalho(): { tone: Tom | 'busy'; label: string; sentence: string | null } {
    if (tentativa !== null) {
      // A frase e o botão vêm do `AttemptActions`; aqui só a pílula.
      const a = describeAttempt(tentativa);
      return { tone: a.tone, label: a.label, sentence: null };
    }
    if (remoto !== null && remoto.degraded !== null) {
      return { tone: 'bad', label: remoto.degraded.label, sentence: null };
    }
    if (remoto !== null && remoto.pendencias.length > 0) {
      const n = remoto.pendencias.length;
      return {
        tone: 'warn',
        label: `Recebe · falta ${String(n)} passo${n === 1 ? '' : 's'} seu${n === 1 ? '' : 's'}`,
        sentence: null,
      };
    }
    return { tone: d.tone, label: d.label, sentence: d.sentence };
  }
  const cab = cabecalho();
  const tom: Tom = cab.tone === 'busy' ? 'warn' : cab.tone;

  return (
    <div className={`mb-3 rounded-lg border border-border-default bg-surface p-4 ${BORDA[tom]}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-text-primary">{nome}</span>
            <span className={`rounded-full border px-2 py-0.5 text-xs ${PILULA[tom]}`}>
              {cab.label}
            </span>
          </div>
          {config.displayName !== null && (
            <div className="mt-0.5 font-mono text-xs text-text-secondary">
              {typeof config.config.displayPhoneNumber === 'string'
                ? `${config.config.displayPhoneNumber} · `
                : ''}
              {config.displayName}
            </div>
          )}
        </div>

        {canManage && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-md border border-border-strong bg-surface px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-muted disabled:opacity-50"
              disabled={testar.isPending}
              onClick={() => {
                testar.mutate(config.id, {
                  onSuccess: (r) => {
                    setUltimoTeste(r.detail);
                  },
                });
              }}
            >
              {testar.isPending ? 'Testando…' : 'Testar conexão'}
            </button>
            {/* Canal do popup: o token veio do cofre, não de um campo — «Trocar credencial»
                não faz sentido; o gesto certo é trocar de número ou desconectar (decisão 3). */}
            {!viaPopup && (
              <button
                type="button"
                className="rounded-md border border-border-strong bg-surface px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-muted"
                onClick={() => {
                  onTrocarCredencial(config);
                }}
              >
                {health.credentialPresent ? 'Trocar credencial' : 'Colocar credencial'}
              </button>
            )}
            {viaPopup && (
              <>
                <button
                  type="button"
                  className="rounded-md border border-border-strong bg-surface px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-muted"
                  onClick={() => {
                    setDialogo('trocar');
                  }}
                >
                  Trocar de número
                </button>
                <button
                  type="button"
                  className="rounded-md border border-border-strong bg-surface px-2.5 py-1 text-xs text-text-secondary hover:bg-bg-muted"
                  onClick={() => {
                    setDialogo('desconectar');
                  }}
                >
                  Desconectar
                </button>
              </>
            )}
            <button
              type="button"
              className={`rounded-md border px-2.5 py-1 text-xs disabled:opacity-50 ${
                confirmandoDesligar
                  ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                  : 'border-border-strong bg-surface text-text-secondary hover:bg-bg-muted'
              }`}
              disabled={interruptor.isPending}
              onClick={() => {
                if (health.enabled && !confirmandoDesligar) {
                  setConfirmandoDesligar(true);
                  return;
                }
                setConfirmandoDesligar(false);
                interruptor.mutate({
                  id: config.id,
                  version: config.version,
                  enabled: !health.enabled,
                });
              }}
            >
              {interruptor.isPending
                ? 'Aplicando…'
                : !health.enabled
                  ? 'Ligar canal'
                  : confirmandoDesligar
                    ? 'Confirmar: parar de atender'
                    : 'Desligar canal'}
            </button>
          </div>
        )}
      </div>

      {/* Tentativa andando: a lista de passos no lugar dos fatos (prancha A5/A6 dentro do canal). */}
      {tentativa !== null && <OnboardingSteps steps={stepsFor(tentativa)} />}

      {tentativa !== null && <AttemptActions attempt={tentativa} canManage={canManage} />}

      {/* A frase vem ANTES da tabela: quem abre esta tela já tem um problema. */}
      {cab.sentence !== null && (
        <div
          className={`mt-3 flex gap-2 rounded-md border px-3 py-2.5 text-xs leading-relaxed ${PILULA[tom]}`}
          role={tom === 'bad' ? 'alert' : undefined}
        >
          <span className="font-bold">{tom === 'bad' ? '✗' : '!'}</span>
          <div className="min-w-0 flex-1">{comDestaque(cab.sentence)}</div>
        </div>
      )}

      {ultimoTeste !== null && (
        <p className="mt-2 text-xs text-text-secondary">Teste agora: {ultimoTeste}</p>
      )}

      {!tentativaAndando && (
        <div className="mt-3 grid grid-cols-1 gap-px overflow-hidden rounded-md border border-border-subtle bg-border-subtle sm:grid-cols-3">
          <div className="bg-surface px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wide text-text-secondary">
              Última entrada
            </div>
            <div
              className={`mt-0.5 text-xs ${
                d.state === 'refusing' ? 'font-medium text-red-700' : 'text-text-primary'
              }`}
            >
              {quando(health.lastInboundAt)}
              {health.lastDeliveryOutcome !== null &&
                ` · ${health.lastDeliveryOutcome === 'recognized' ? 'aceita' : 'recusada'}`}
            </div>
          </div>
          <div className="bg-surface px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wide text-text-secondary">
              {viaPopup ? 'Acesso à Meta' : 'Credencial'}
            </div>
            <div
              className={`mt-0.5 text-xs ${
                health.credentialPresent ? 'text-text-primary' : 'font-medium text-red-700'
              }`}
            >
              {!health.credentialPresent
                ? 'ausente'
                : viaPopup
                  ? // Veio do popup: «trocada» não é o verbo; foi autorizado pelo tenant na Meta.
                    `autorizado ${quando(health.credentialRotatedAt)} · guardado no cofre`
                  : health.credentialRotatedAt === null
                    ? // Credencial que veio de seed ou de `.env` nunca passou pelo cofre, então
                      // não há data de troca. «trocada nunca» seria frase sem sentido.
                      'presente · nunca trocada por aqui'
                    : `presente · trocada ${quando(health.credentialRotatedAt)}`}
            </div>
          </div>
          <div className="bg-surface px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wide text-text-secondary">
              Último teste
            </div>
            <div
              className={`mt-0.5 text-xs ${
                health.lastTestResult !== null && health.lastTestResult !== 'ok'
                  ? 'font-medium text-red-700'
                  : 'text-text-primary'
              }`}
            >
              {describeTest(health)}
              {health.lastTestAt !== null && ` · ${quando(health.lastTestAt)}`}
            </div>
          </div>
        </div>
      )}

      {!tentativaAndando && ehWhatsApp && health.remote !== null && (
        <RemoteHealthBlock
          config={config}
          remote={health.remote}
          canManage={canManage}
          onReconectar={() => {
            setDialogo('trocar');
          }}
        />
      )}

      {!tentativaAndando && health.webhookExpectedUrl !== '' && (
        <p className="mt-2.5 text-xs text-text-secondary">
          Webhook que esperamos que o provedor tenha:{' '}
          <span className="break-all rounded border border-border-default bg-bg-muted px-1.5 py-0.5 font-mono">
            {health.webhookExpectedUrl}
          </span>
        </p>
      )}

      {dialogo !== null && (
        <DisconnectDialog
          config={config}
          modo={dialogo}
          onClose={() => {
            setDialogo(null);
          }}
          onDone={() => {
            // Trocar de número segue direto para a escolha do caminho do número novo (revisão
            // de 30/Set, menor 7) — antes parava no cartão «Não conectado».
            if (dialogo === 'trocar') onTrocouNumero?.();
            setDialogo(null);
          }}
        />
      )}
    </div>
  );
}
